import { useRef, useState, useEffect } from "react";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "../api/axios";
import { useNavigate } from "react-router-dom";
import { Container, Form, Toast, ToastContainer } from "react-bootstrap";

const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%]).{8,24}$/;
const REGISTER_URL = "/api/v1/users/user";

const Register = () => {
  const userRef = useRef();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [validEmail, setValidEmail] = useState(false);
  const [emailFocus, setEmailFocus] = useState(false);

  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");

  const [pwd, setPwd] = useState("");
  const [validPwd, setValidPwd] = useState(false);
  const [pwdFocus, setPwdFocus] = useState(false);

  const [matchPwd, setMatchPwd] = useState("");
  const [validMatch, setValidMatch] = useState(false);
  const [matchFocus, setMatchFocus] = useState(false);

  const [terms, setTerms] = useState(false);

  const [errMsg, setErrMsg] = useState("");
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    userRef.current?.focus();
  }, []);

  useEffect(() => {
    setValidEmail(EMAIL_REGEX.test(email));
  }, [email]);

  useEffect(() => {
    setValidPwd(PWD_REGEX.test(pwd));
    setValidMatch(pwd === matchPwd);
  }, [pwd, matchPwd]);

  useEffect(() => {
    setErrMsg("");
  }, [email, pwd, matchPwd]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // if button enabled with JS hack
    if (submitting) return;
    const v1 = EMAIL_REGEX.test(email);
    const v2 = PWD_REGEX.test(pwd);
    if (!v1 || !v2) {
      setErrMsg("Invalid Entry");
      return;
    }
    try {
      setSubmitting(true);
      const response = await axios.post(
        REGISTER_URL,
        JSON.stringify({
          email: email,
          password: pwd,
          confirmPassword: matchPwd,
          name,
          lastName,
        }),
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      );
      // TODO: remove console.logs before deployment
      console.log(JSON.stringify(response?.data));
      //console.log(JSON.stringify(response))
      setSuccess(true);
      //clear state and controlled inputs
      setEmail("");
      setName("");
      setLastName("");
      setPwd("");
      setMatchPwd("");
    } catch (err) {
      if (!err?.response) {
        setErrMsg("No Server Response");
      } else if (err.response?.status === 409) {
        setErrMsg("Username Taken");
      } else {
        setErrMsg("Registration Failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {success ? (
        <Container className="my-3 text-center">
          <h1>Success!</h1>
          <p>
            Your registration is almost completed.
            <br />
            <br />
            Please go to your email inbox and find the message from ISIDE Users
            Registration Service. Open it and click the link to confirm that the
            email address is correct.
          </p>
          <p>
            <div
              className="custom-button primary"
              onClick={() => {
                navigate("/login");
              }}
            >
              Sign In
            </div>
          </p>
        </Container>
      ) : (
        <Container className="my-3 ">
          <ToastContainer className="p-3" position="top-end">
            <Toast
              bg="danger"
              onClose={() => setErrMsg("")}
              show={errMsg !== ""}
              delay={5000}
              autohide
            >
              <Toast.Body className="text-white">{errMsg}</Toast.Body>
            </Toast>
          </ToastContainer>
          <h1 className="text-center">Register</h1>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="email">
              <Form.Label>Email:</Form.Label>
              <Form.Control
                type="email"
                ref={userRef}
                autoComplete="off"
                onChange={(e) => setEmail(e.target.value)}
                value={email}
                isValid={validEmail}
                isInvalid={email && !validEmail}
                required
                aria-invalid={validEmail ? "false" : "true"}
                aria-describedby="emailHelpBlock"
                onFocus={() => setEmailFocus(true)}
                onBlur={() => setEmailFocus(false)}
              />
              <Form.Text
                id="emailHelpBlock"
                className={emailFocus && email && !validEmail ? "" : "d-none"}
                muted
              >
                <FontAwesomeIcon icon={faInfoCircle} /> Must be a valid email
                address. Validation is required.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="name">
              <Form.Label>Name:</Form.Label>
              <Form.Control
                type="text"
                ref={userRef}
                autoComplete="off"
                onChange={(e) => setName(e.target.value)}
                value={name}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="lastname">
              <Form.Label>Lastname:</Form.Label>
              <Form.Control
                type="text"
                ref={userRef}
                autoComplete="off"
                onChange={(e) => setLastName(e.target.value)}
                value={lastName}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="password">
              <Form.Label>Password:</Form.Label>
              <Form.Control
                type="password"
                onChange={(e) => setPwd(e.target.value)}
                value={pwd}
                isValid={validPwd}
                isInvalid={pwd && !validPwd}
                required
                aria-invalid={validPwd ? "false" : "true"}
                aria-describedby="passwordlHelpBlock"
                onFocus={() => setPwdFocus(true)}
                onBlur={() => setPwdFocus(false)}
              />
              <Form.Text
                id="passwordlHelpBlock"
                className={pwdFocus && pwd && !validPwd ? "" : "d-none"}
                muted
              >
                <FontAwesomeIcon icon={faInfoCircle} /> Your password must be
                8-24 characters long, must include uppercase and lowercase
                letters, a number and a special character. Allowed special
                characters: <span aria-label="exclamation mark">!</span>{" "}
                <span aria-label="at symbol">@</span>{" "}
                <span aria-label="hashtag">#</span>{" "}
                <span aria-label="dollar sign">$</span>{" "}
                <span aria-label="percent">%</span>
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="confirm_pwd">
              <Form.Label>Confirm Password:</Form.Label>
              <Form.Control
                type="password"
                onChange={(e) => setMatchPwd(e.target.value)}
                value={matchPwd}
                isValid={validMatch}
                isInvalid={matchPwd && !validMatch}
                required
                aria-invalid={validMatch ? "false" : "true"}
                aria-describedby="confirmHelpBlock"
                onFocus={() => setMatchFocus(true)}
                onBlur={() => setMatchFocus(false)}
              />
              <Form.Text
                id="confirmHelpBlock"
                className={
                  matchFocus && matchPwd && !validMatch ? "" : "d-none"
                }
                muted
              >
                <FontAwesomeIcon icon={faInfoCircle} /> Must match the first
                password input field.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                required
                name="terms"
                onChange={(e) => setTerms(e.target.checked)}
                value={terms}
                isValid={terms}
                label="Agree to terms and conditions"
                feedback="You must agree before submitting."
                feedbackType="invalid"
              />
            </Form.Group>
            <p className="text-center">
              <div
                className="custom-button primary"
                onClick={handleSubmit}
                disabled={
                  submitting ||
                  !validEmail ||
                  !validPwd ||
                  !validMatch ||
                  !terms
                    ? true
                    : false
                }
              >
                Sign Up
              </div>
            </p>
          </Form>
          <p className="mt-3 text-center">
            Already registered?
            <br />
            <div
              className="custom-button primary"
              onClick={() => {
                navigate("/login");
              }}
            >
              Sign In
            </div>
          </p>
        </Container>
      )}
    </>
  );
};

export default Register;
